import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getWebApp, haptic, openExternalUrl } from '@/lib/telegram';
import { api } from '@/lib/api';
import { Loader2, AlertTriangle, Clock, XCircle, Star } from 'lucide-react';
import { IconChip } from '@/components/ui/calm';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';

// skeleton-ok: экран ожидания платежа, а не загрузки данных. Скелетон изображал бы
// содержимое, которого ещё нет ни на сервере: тут честнее спиннер с текстом.
export const Route = createFileRoute('/processing/$paymentId')({
  component: Processing,
});

const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function Processing() {
  const { paymentId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();

  useEffect(() => {
    const tg = getWebApp();
    tg?.MainButton.hide();
    tg?.BackButton.hide();
  }, []);

  const startedAtRef = useRef<number>(Date.now());
  const [timedOut, setTimedOut] = useState(false);

  const { data: status, error, refetch, isError } = useQuery({
    queryKey: ['payment-status', paymentId],
    queryFn: () => api.paymentStatus(paymentId),
    refetchInterval: (q) => {
      if (q.state.error) return false; // не дёргаем дальше при ошибке — юзеру предложим retry
      const s = q.state.data?.status;
      const f = q.state.data?.fulfillment;
      // Оплачено, но подписка ещё не выдана — продолжаем поллить: её до-создаёт
      // recovery на бэке, и экран сам переключится на success. Раньше поллинг тут
      // останавливался, и человек навсегда оставался на молчаливом спиннере.
      const awaitingFulfillment = s === 'paid' && (f === 'pending' || f === 'deferred');
      if (s !== 'pending' && !awaitingFulfillment) return false;
      if (Date.now() - startedAtRef.current > POLL_TIMEOUT_MS) {
        setTimedOut(true);
        return false;
      }
      return awaitingFulfillment ? 5000 : 2000;
    },
    retry: 3,
  });

  const intentType = status?.intent_type ?? null;
  const isTopup = intentType === 'topup' || (intentType === null && status?.subscription_id == null && status?.status === 'paid');

  useEffect(() => {
    if (status?.status !== 'paid') return;
    track('payment_succeeded', { payment_id: paymentId, intent_type: intentType });
    // После оплаты данные клиента изменились (подписки, баланс, устройства, история).
    // Глобальный staleTime=5мин иначе отдаёт downstream-экранам устаревший кэш — из-за
    // этого новая подписка/конфиг «появлялись только после перезахода», а QR на success
    // был пустой (configUrl из протухшего ['subs']). Инвалидируем всё разом.
    void qc.invalidateQueries();
    if (isTopup) {
      // Topup не создаёт subscription — деньги уже на client:balance USD.
      // Возвращаем юзера в профиль где он увидит обновлённый баланс.
      navigate({ to: '/profile' });
      return;
    }
    if (intentType === 'addon' && status.subscription_id) {
      // Докупка к «Навсегда» применена: конфиг прежний, настраивать нечего. Возвращаем
      // в подписку, где уже видно новый лимит.
      toast.success(t('Addon applied'));
      navigate({ to: '/me' });
      return;
    }
    if (status.subscription_id) {
      // Subscribe / renew / upgrade — есть subscription_id, ведём на success-page (QR + connect).
      navigate({
        to: '/success/$subscriptionId',
        params: { subscriptionId: status.subscription_id },
      });
    }
  }, [status, navigate, paymentId, intentType, isTopup, qc, t]);

  const phase: 1 | 2 | 3 =
    status?.status === 'paid' && status.subscription_id ? 3 : status?.status === 'paid' ? 2 : 1;
  const failed = status?.status === 'failed' || status?.status === 'expired';

  if (isError && !status) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center space-y-6">
        <IconChip icon={AlertTriangle} tone="red" size={64} radius={18} />
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Payment check failed')}</h2>
        <p className="text-[#8a8c93] text-sm">
          {(error as Error)?.message || t('Payment check failed desc')}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="calm-cta px-6 py-3 rounded-xl text-sm font-bold"
          >
            {t('Retry')}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: '/' })}
            className="calm-card text-[#c2c4ca] px-6 py-3 rounded-xl text-sm font-bold"
          >
            {t('To home')}
          </button>
        </div>
      </div>
    );
  }

  if (timedOut && status?.status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center space-y-6">
        <IconChip icon={Clock} tone="amber" size={64} radius={18} />
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Payment pending')}</h2>
        <p className="text-[#8a8c93]">
          {t('Payment pending desc')}
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: '/' })}
          className="calm-cta px-6 py-3 rounded-xl text-sm font-bold"
        >
          {t('To home')}
        </button>
      </div>
    );
  }

  // Деньги дошли, но подписку выдать пока не удалось (у платформы кончился баланс
  // у провайдера сети и т.п.). Главное сказать человеку: оплата не потеряна, она уже
  // на балансе, и подписка активируется сама — платить второй раз не нужно.
  if (status?.status === 'paid' && status.fulfillment === 'deferred') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center space-y-6">
        <IconChip icon={Clock} tone="amber" size={64} radius={18} />
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">
          {t('Paid sub coming')}
        </h2>
        <p className="text-[#8a8c93] text-sm leading-relaxed">
          {t('Paid sub coming desc')}
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            className="calm-cta px-6 py-3 rounded-xl text-sm font-bold"
          >
            {t('Check again now')}
          </button>
          <button
            type="button"
            onClick={() => navigate({ to: '/' })}
            className="calm-card text-[#c2c4ca] px-6 py-3 rounded-xl text-sm font-bold"
          >
            {t('To home')}
          </button>
        </div>
      </div>
    );
  }

  if (failed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center space-y-6">
        <IconChip icon={XCircle} tone="red" size={64} radius={18} />
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Payment failed')}</h2>
        <p className="text-[#8a8c93]">
          {status?.status === 'expired' ? t('Invoice expired') : t('Invoice cancelled')}
        </p>
        <button
          type="button"
          onClick={() => navigate({ to: '/plans' })}
          className="calm-cta px-6 py-3 rounded-xl text-sm font-bold"
        >
          {t('To plans')}
        </button>
      </div>
    );
  }

  // Шаги в processing-экране зависят от типа платежа. Для topup нет «создаём подписку».
  const steps: Array<{ n: number; text: string }> = (() => {
    if (isTopup) {
      return [
        { n: 1, text: t('Verifying payment') },
        { n: 2, text: t('Step crediting balance') },
      ];
    }
    if (intentType === 'traffic') {
      return [
        { n: 1, text: t('Verifying payment') },
        { n: 2, text: t('Step crediting traffic') },
      ];
    }
    if (intentType === 'addon') {
      return [
        { n: 1, text: t('Verifying payment') },
        { n: 2, text: t('Step applying addon') },
      ];
    }
    if (intentType === 'renew') {
      return [
        { n: 1, text: t('Verifying payment') },
        { n: 2, text: t('Step renewing') },
      ];
    }
    if (intentType === 'gift') {
      return [
        { n: 1, text: t('Verifying payment') },
        { n: 2, text: t('Step preparing gift') },
      ];
    }
    // subscribe / upgrade / unknown — полный 3-шаговый flow с conf
    return [
      { n: 1, text: t('Verifying payment') },
      { n: 2, text: t('Creating subscription') },
      { n: 3, text: t('Preparing config') },
    ];
  })();

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center space-y-8">
      <Loader2 className="w-16 h-16 animate-spin text-accent" />
      <div className="space-y-2">
        <h2 className="text-[25px] font-extrabold tracking-[-0.8px] text-foreground">{t('Processing Payment')}</h2>
        <p className="text-[#8a8c93]">{t('Processing hint')}</p>
      </div>

      {status?.status === 'pending' && status.stars_shop_url && status.stars ? (
        <StarsShopCard url={status.stars_shop_url} stars={status.stars} />
      ) : null}

      <div className="w-full max-w-xs space-y-4 text-left">
        {steps.map((s) => (
          <Step
            key={s.n}
            number={s.n}
            text={s.text}
            active={phase >= s.n}
            current={phase === s.n}
          />
        ))}
      </div>
    </div>
  );
}

/** Оплата звёздами: «1. Купить звёзды · 2. Оплатить». Первая кнопка ведёт в бота
 *  партнёра на Хексе сразу на нужное количество. Сколько звёзд у человека, Telegram
 *  не говорит, поэтому карточка видна всегда, пока счёт не оплачен. */
function StarsShopCard({ url, stars }: { url: string; stars: number }) {
  const { t } = useTranslation();
  return (
    <div className="w-full max-w-xs rounded-2xl calm-card p-4 text-left">
      <div className="text-[13.5px] font-bold text-foreground">{t('Stars shop title')}</div>
      <ol className="mt-2.5 space-y-2 text-[12.5px] leading-snug text-[#c2c4ca]">
        <li className="flex gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/8 text-[11px] font-bold tabular-nums">1</span>
          <span className="min-w-0">{t('Stars shop step buy', { count: stars })}</span>
        </li>
        <li className="flex gap-2.5">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/8 text-[11px] font-bold tabular-nums">2</span>
          <span className="min-w-0">{t('Stars shop step pay')}</span>
        </li>
      </ol>
      <button
        type="button"
        onClick={() => {
          haptic('light');
          openExternalUrl(url);
        }}
        className="mt-3 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl calm-cta text-[13.5px] font-bold active:scale-[0.98] transition-transform"
      >
        <Star className="h-4 w-4" strokeWidth={2.2} />
        {t('Buy stars')}
      </button>
    </div>
  );
}

function Step({ number, text, active, current }: { number: number; text: string; active: boolean; current: boolean }) {
  return (
    <div className={cn('flex items-center gap-3 transition-opacity', active ? 'opacity-100' : 'opacity-40')}>
      <div
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold',
          active ? 'calm-cta' : 'bg-white/8 text-[#8a8c93]',
        )}
      >
        {number}
      </div>
      <span className={cn('font-medium', current ? 'text-accent' : 'text-foreground')}>{text}</span>
    </div>
  );
}
